import { createElement } from "react";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";

import { buildDocHref } from "../content/href";
import { listVisibleTabs, resolveTab } from "../content/registry";
import { resolveDoc } from "../content/resolveDoc";
import { resolveLanguage } from "../locale/resolveLanguage";
import { useDocsPathname } from "./DocsPathnameContext";
import classes from "./docsHeader.module.css";

/**
 * DocsHeaderTabs is the header's second row and the site's top-level information
 * architecture: one link per section, in reading order, each a full-reload anchor
 * to its landing page since the docs app serves static pages with no client router.
 * The section that owns the current pathname is marked, drawing its underline on
 * the header's own bottom border so the strip reads as a set of tabs over the page.
 *
 * Only sections whose landing page resolves are listed, so a section declared in
 * the curated tree ahead of its content is simply absent until it is authored.
 *
 * The strip scrolls sideways on narrow viewports rather than folding into a menu.
 * Every section stays reachable at every width, including on the pages framed
 * without a sidebar, which carry no other navigation.
 */
export const DocsHeaderTabs = () => {
  const { t } = useTranslation("docs");
  const pathname = useDocsPathname();
  const language = resolveLanguage(pathname);
  const activeTab = resolveTab(pathname);

  // Tab ownership is a total function — an unclaimed slug falls back to the
  // Documentation tab — so the resolved tab alone would mark Documentation active
  // on a location that has no page at all. A dead URL is inside no section, so the
  // not-found view carries no active tab and the strip reads as the plain
  // information architecture it is.
  const hasPage = resolveDoc(pathname).doc !== null;

  return (
    <nav aria-label={t("nav.sections")} className={classes.tabRow}>
      {listVisibleTabs(language).map((tab) => (
        <a
          className={classes.tab}
          data-active={hasPage && tab.id === activeTab.id ? true : undefined}
          href={buildDocHref(language, tab.landingSlug)}
          key={tab.id}
        >
          {createElement(tab.icon, { size: ICON_SIZE.sm })}
          {t(tab.labelKey)}
        </a>
      ))}
    </nav>
  );
};
